import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, CheckCircle2, Send, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BUREAU_COLORS } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function ReportUpload() {
  const { apiErr } = useAuth();
  const [consumers, setConsumers] = useState([]);
  const [consumerId, setConsumerId] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef();

  useEffect(() => { api.get("/consumers").then(({ data }) => setConsumers(data)); }, []);

  const pick = (f) => {
    if (!f) return;
    const ok = ["pdf", "png", "jpg", "jpeg", "webp", "csv"].includes(f.name.split(".").pop().toLowerCase());
    if (!ok) { toast.error("Use PDF, image (JPG/PNG) or CSV"); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!consumerId) { toast.error("Select a consumer to match"); return; }
    if (!file) { toast.error("Choose a report file"); return; }
    setUploading(true); setReport(null);
    const fd = new FormData();
    fd.append("consumerId", consumerId);
    fd.append("file", file);
    try {
      const { data } = await api.post("/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setReport(data);
      if (data.parseError) toast.warning(data.parseError);
      else if ((data.accounts || []).length === 0) toast.info("No negative tradelines detected in this file");
      else toast.success(`Extracted ${data.accounts.length} negative item(s)`);
      if (data.scoresImported > 0) toast.success(`${data.scoresImported} credit score(s) imported to the consumer`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const toggle = (id) => setReport((r) => ({ ...r, accounts: r.accounts.map((a) => a.id === id ? { ...a, selected: !a.selected } : a) }));

  const createDisputes = async () => {
    const selected = report.accounts.filter((a) => a.selected);
    if (selected.length === 0) { toast.error("Toggle at least one item to dispute"); return; }
    setCreating(true);
    try {
      for (const a of selected) {
        await api.post("/disputes", {
          consumerId: report.consumerId, consumerName: report.consumerName, bureau: a.bureau,
          creditor: a.creditor, accountNumber: a.accountNumber, amount: a.balance, urgency: "High",
          disputeType: "FCRA § 605B Identity Theft Block", status: "Drafted",
          notes: `Fraud reason: ${a.negativeType} — ${a.reason}`,
        });
      }
      toast.success(`${selected.length} item(s) sent to disputes & letters`);
      setReport(null); setFile(null); setConsumerId("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const disputeCount = report ? report.accounts.filter((a) => a.selected).length : 0;

  return (
    <div className="space-y-6" data-testid="upload-panel">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <label className="text-sm text-neutral-600 mb-2 block">Match report to consumer</label>
          <Select value={consumerId} onValueChange={setConsumerId}>
            <SelectTrigger className="bg-[#F6EEEE] border-neutral-200" data-testid="report-consumer-selector">
              <SelectValue placeholder="Select consumer…" />
            </SelectTrigger>
            <SelectContent>
              {consumers.map((c) => <SelectItem key={c.id} value={c.id}>{c.fullName}</SelectItem>)}
            </SelectContent>
          </Select>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`mt-4 rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragging ? "border-[#C1121F] bg-[#C1121F]/5" : "border-neutral-200 hover:border-white/30"}`}
            data-testid="credit-report-dropzone">
            <input ref={inputRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.csv" onChange={(e) => pick(e.target.files[0])} data-testid="report-file-input" />
            <UploadCloud className="h-10 w-10 mx-auto text-[#C1121F]" />
            <p className="mt-3 font-medium">{file ? file.name : "Drop credit report or click to browse"}</p>
            <p className="mt-1 text-xs text-neutral-400">PDF · JPG · PNG · CSV</p>
          </div>

          <Button onClick={upload} disabled={uploading}
            className="mt-4 w-full rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold"
            data-testid="submit-report-upload-button">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Parsing report…</> : "Upload & Extract Tradelines"}
          </Button>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <div className="flex items-center gap-2 text-[#C1121F]"><AlertTriangle className="h-5 w-5" /><span className="font-semibold">How matching works</span></div>
          <p className="mt-3 text-sm text-neutral-500 leading-relaxed">
            Upload a consumer's credit report and we securely store the file, then extract each suspected
            fraudulent tradeline — creditor, account number, reporting bureau, balance, and fraud reason.
            Select the accounts to instantly convert them into § 605B dispute packets in the tracking pipeline.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {["Never Authorized", "Synthetic Identity", "Unauthorized Inquiry", "Account Takeover"].map((r) => (
              <span key={r} className="px-2.5 py-1 rounded-md bg-neutral-50 border border-neutral-200 text-neutral-600">{r}</span>
            ))}
          </div>
        </div>
      </div>

      {report && (
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6" data-testid="parsed-tradelines-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[#15803D]" />
              <span className="font-semibold">Negative tradelines from {report.original_filename} · {report.consumerName}</span>
            </div>
            <Button onClick={createDisputes} disabled={creating}
              className="rounded-full bg-[#15803D] hover:bg-[#1E9E4B] text-white font-semibold"
              data-testid="create-disputes-from-report-button">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Dispute {disputeCount} to Bureau
            </Button>
          </div>
          <p className="mt-2 text-xs text-neutral-400">Toggle each item ON to include it on the dispute letter to the bureau, or OFF to leave it off.</p>
          {report.scoresImported > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="text-neutral-500">Scores imported:</span>
              {(report.scores || []).map((s, i) => (
                <span key={i} className="px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 font-mono">{s.bureau} {s.score}{s.date ? ` · ${s.date}` : ""}</span>
              ))}
            </div>
          )}
          {report.accounts.length === 0 && (
            <p className="mt-4 text-sm text-neutral-400">No negative tradelines were detected in this document.</p>
          )}

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="parsed-tradelines-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-400 border-b border-neutral-200">
                  <th className="py-3 pr-3">Dispute</th><th className="py-3 pr-3">Creditor</th><th className="py-3 pr-3">Type</th>
                  <th className="py-3 pr-3">Account</th><th className="py-3 pr-3">Bureau</th><th className="py-3 pr-3">Balance</th><th className="py-3 pr-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {report.accounts.map((a) => {
                  const bc = BUREAU_COLORS[a.bureau] || BUREAU_COLORS.Furnisher;
                  return (
                    <tr key={a.id} className={`border-b border-neutral-100 ${a.selected ? "" : "opacity-50"}`}>
                      <td className="py-3 pr-3"><Switch checked={a.selected} onCheckedChange={() => toggle(a.id)} data-testid={`tradeline-toggle-${a.id}`} /></td>
                      <td className="py-3 pr-3 font-medium">{a.creditor}</td>
                      <td className="py-3 pr-3"><span className="px-2 py-0.5 rounded-md bg-rose-500/10 text-rose-600 border border-rose-500/20 text-xs">{a.negativeType}</span></td>
                      <td className="py-3 pr-3 font-mono text-neutral-500">{a.accountNumber}</td>
                      <td className="py-3 pr-3"><span className={`px-2 py-0.5 rounded-md border text-xs ${bc.chip} ${bc.text} ${bc.border}`}>{a.bureau}</span></td>
                      <td className="py-3 pr-3 font-mono text-[#9B0E18]">{a.balance}</td>
                      <td className="py-3 pr-3 text-neutral-500">{a.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
